import React, { useEffect, useState } from "react";
import { Typography, Grid, Card, CardContent, IconButton, Tooltip, Input, useMediaQuery, useTheme, Box } from "@mui/material";
import { AttachFile, Visibility, Delete } from "@mui/icons-material";
import styled from "styled-components";
import { useTranslation } from "react-i18next";

const HiddenInput = styled("input")({
    display: "none",
});

const UploadCard = styled(Card)(({ theme, isDragging, isUploaded, disabled }) => ({
    width: "100%",
    height: "100%",
    minHeight: "200px",
    borderRadius: "16px",
    boxShadow: "none !important",
    transition: "all 0.3s ease",
    backgroundColor: disabled
        ? "rgba(158, 158, 158, 0.1)"
        : isUploaded
            ? "rgba(255, 255, 255, 0.3)"
            : "rgba(255, 255, 255, 0.2)",
    border: "2px dashed",
    borderColor: disabled
        ? "#e0e0e0"
        : isDragging
            ? '#0017E7'
            : isUploaded
                ? '#0017E7'
                : '#D9D9D9',
    transition: "all 0.3s ease",
    cursor: disabled ? "not-allowed" : "pointer",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    transform: isDragging && !disabled ? "scale(1.02)" : "scale(1)",
    opacity: disabled ? 0.6 : 1,
    "&:hover": {
        transform: disabled ? "scale(1)" : "translateY(-4px)",
        boxShadow: "none !important",
        backgroundColor: disabled
            ? "rgba(158, 158, 158, 0.1)"
            : "rgba(255, 255, 255, 0.35)",
    },
    "& .MuiCard-root": {
        boxShadow: "none !important"
    }
}));

const IconWrapper = styled("div")(({ theme, isUploaded, disabled }) => ({
    "& .MuiSvgIcon-root": {
        fontSize: 48,
        marginBottom: 2,
        color: disabled
            ? "#bdbdbd"
            : isUploaded
                ? "#0017E7"
                : "#9e9e9e",
    },
}));

const FileUploadCard = ({ type, isUploaded, register, handleFileUpload, fileName, selectedFile, selectedFilename, disabled }) => {
    const { t } = useTranslation();
    const [isDragging, setIsDragging] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));
    console.log('disabled:',disabled)
    useEffect(() => {
        console.log("selectedFile", selectedFile);
    }, [selectedFile]);

    const handleDrag = (e) => {
        if (disabled) return;
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDragIn = (e) => {
        if (disabled) return;
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragOut = (e) => {
        if (disabled) return;
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
    };

    const handleDrop = (e) => {
        if (disabled) return;
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);

        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            const syntheticEvent = {
                target: {
                    files: files,
                },
            };
            handleFileUpload(syntheticEvent, type);
        }
    };

    const isResume = type === "resume";
    const title = isResume ? t("applicationForm.resume") : t("applicationForm.coverLetter");
    const subtitle = isResume ? t("applicationForm.uploadResume") : t("applicationForm.uploadCoverLetter");

    return (
        <Grid item xs={12} sm={12} md={12} sx={{ paddingX: { xs: 4, md: 0 } }}>
            <HiddenInput
                id={`${type}-upload`}
                type="file"
                disabled={disabled}
                {...register(type, {
                    onChange: (e) => !disabled && handleFileUpload(e, type),
                })}
            />
            <label 
                htmlFor={disabled ? undefined : `${type}-upload`}
                style={{ 
                    display: "block", 
                    height: "100%",
                    cursor: disabled ? "not-allowed" : "pointer"
                }}
                onDragOver={handleDrag}
                onDragEnter={handleDragIn}
                onDragLeave={handleDragOut}
                onDrop={handleDrop}
            >
                <UploadCard isDragging={isDragging} isUploaded={isUploaded} disabled={disabled}>
                    <CardContent sx={{
                        padding: isMobile ? "16px" : "24px",
                        textAlign: "center",
                    }}>
                        <IconWrapper isUploaded={isUploaded} disabled={disabled} sx={{
                            transform: isMobile ? 'scale(0.8)' : 'scale(1)',
                        }}>
                            <AttachFile sx={{
                                color: disabled
                                    ? "#bdbdbd"
                                    : isUploaded
                                        ? "#0017E7"
                                        : "#9e9e9e",
                                fontSize: isMobile ? "36px" : "48px",
                            }} />
                        </IconWrapper>
                        <Typography
                            variant={isMobile ? "subtitle1" : "h6"}
                            sx={{
                                fontWeight: 600,
                                color: disabled
                                    ? "#bdbdbd"
                                    : isUploaded
                                        ? "#0017E7"
                                        : "#9e9e9e",
                                mb: 1,
                                fontSize: isMobile ? "1rem" : isTablet ? "1.05rem" : "1.1rem",
                            }}
                        >
                            {isUploaded ? (
                                <>
                                    {title} {t("applicationForm.uploaded")}
                                    {selectedFilename && (
                                        <Box>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    mt: isMobile ? 1 : 2,
                                                    color: disabled ? "#bdbdbd" : "#0017E7",
                                                    backgroundColor: disabled
                                                        ? "rgba(189, 189, 189, 0.1)"
                                                        : "rgba(0, 23, 231, 0.1)",
                                                    borderRadius: "8px",
                                                    padding: isMobile ? "4px 8px" : "6px 12px",
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    gap: isMobile ? "4px" : "8px",
                                                    maxWidth: "100%",
                                                    overflow: "hidden",
                                                    wordBreak: "break-word",
                                                    flexWrap: isMobile ? "wrap" : "nowrap",
                                                }}
                                            >
                                                <AttachFile sx={{
                                                    fontSize: isMobile ? "14px !important" : "16px !important",
                                                    flexShrink: 0,
                                                    color: disabled ? "#bdbdbd" : "#0017E7",
                                                }} />
                                                <span style={{
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: isMobile ? "normal" : "nowrap",
                                                    maxWidth: isMobile ? "100%" : "calc(100% - 24px)",
                                                }}>
                                                    {selectedFilename}
                                                </span>
                                            </Typography>
                                        </Box>
                                    )}
                                </>
                            ) : (
                                <>
                                    {isDragging ? t("applicationForm.dropHere") : `${t("applicationForm.uploadYour")} ${title}`}
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: disabled ? "#bdbdbd" : "#9e9e9e",
                                            mt: 1,
                                            fontSize: isMobile ? "0.75rem" : "0.875rem",
                                        }}
                                    >
                                        {subtitle}
                                    </Typography>
                                </>
                            )}
                        </Typography>
                    </CardContent>
                </UploadCard>
            </label>
        </Grid>
    );
};

export default FileUploadCard;